import { useSelector } from "@tanstack/react-form";
import { useEffect, useState, type ComponentProps } from "react";
import { useFieldContext } from "./context";

type BoundProperty =
  | "name"
  | "value"
  | "values"
  | "checked"
  | "defaultValue"
  | "defaultChecked"
  | "error"
  | "onInput"
  | "onChange"
  | "onBlur"
  | "ref";

export type FieldProps<T extends keyof React.JSX.IntrinsicElements> = Omit<
  ComponentProps<T>,
  BoundProperty
>;

function errorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error)
    return typeof error.message === "string" ? error.message : "";
  return "";
}

export function useBoundField<T>() {
  const field = useFieldContext<T>();
  const attempts = useSelector(field.form.store, (state) => state.submissionAttempts);
  // 报错显示后，重新聚焦或输入先收起，等失焦或再次提交时按新值重算再显示。
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => setDismissed(false), [attempts]);
  const error =
    (field.state.meta.isTouched || attempts > 0) && !dismissed
      ? [...new Set(field.state.meta.errors.map(errorMessage).filter(Boolean))].join(" ")
      : "";
  return {
    field,
    error,
    dismissError: () => setDismissed(true),
    handleBlur: () => {
      setDismissed(false);
      field.handleBlur();
    },
  };
}
